import { useToi } from '../../../lib/auth.js';
import { CanhBaoDuLieuThat, HopXacNhan } from '../../../ui/index.js';
import { duongDanXuatExcelKenh, useXemTruocXoaKenh, useXoaKenh } from '../api.js';

/**
 * Hộp thoại xoá vĩnh viễn một kênh.
 *
 * Trước khi cho bấm, hộp thoại nói rõ:
 * - **bao nhiêu dòng mỗi loại** sẽ mất (số lấy từ máy chủ, mỗi module tự đếm);
 * - nhắc chạy `npm run backup:db`;
 * - có nút tải Excel dữ liệu kênh (số liệu ngày, doanh thu, sao kê);
 * - lý do **chặn** nếu kênh còn sao kê đã qua bước đối soát.
 *
 * Người dùng phải gõ lại đúng tên kênh. Máy chủ kiểm lại tất cả, không tin
 * giao diện.
 */
export interface HopXoaKenhProps {
  channelId: string;
  tenKenh: string;
  onDong: () => void;
  /** Gọi khi kênh đã xoá xong — trang gọi dùng để rời khỏi hồ sơ kênh. */
  onXong: () => void;
}

export function HopXoaKenh({ channelId, tenKenh, onDong, onXong }: HopXoaKenhProps) {
  const toi = useToi();
  const xemTruoc = useXemTruocXoaKenh(channelId, true);
  const xoa = useXoaKenh(channelId);

  const loai = Object.entries(xemTruoc.data?.soDong ?? {}).filter(([, so]) => so > 0);
  const chanBoi = xemTruoc.data?.chanBoi ?? [];
  const xoaDuoc = xemTruoc.data?.xoaDuoc ?? false;

  return (
    <HopXacNhan
      nguyHiem
      title={`Xoá vĩnh viễn kênh ${tenKenh}?`}
      nhanXacNhan="Xoá vĩnh viễn"
      goLai={{ nhan: 'Gõ lại đúng tên kênh để xác nhận', giaTri: tenKenh }}
      /*
       * Máy chủ đã nói không xoá được (còn sao kê đã đối soát, kênh chưa lưu
       * trữ) hoặc chưa đếm xong: tắt nút và ẩn ô gõ tên, vì gõ đúng tên cũng
       * không xoá được.
       */
      chan={!xoaDuoc}
      dangChay={xoa.isPending}
      loi={xoa.error?.message ?? xemTruoc.error?.message ?? null}
      onDong={onDong}
      onXacNhan={() => {
        if (!xoaDuoc) return;
        xoa.mutate({ tenXacNhan: tenKenh }, { onSuccess: onXong });
      }}
    >
      <CanhBaoDuLieuThat youtubeMode={toi.data?.youtubeMode} />

      <p>
        Việc này <strong>không lùi được</strong>. Toàn bộ dữ liệu của kênh bị xoá khỏi database.
      </p>

      {xemTruoc.isPending && <p>Đang đếm dữ liệu sẽ mất…</p>}

      {chanBoi.length > 0 && (
        <div className="rounded-[10px] border border-vien bg-bad-nen p-3 text-bad-chu">
          <p className="font-semibold">Không xoá được kênh này</p>
          <ul className="mt-1 list-inside list-disc">
            {chanBoi.map((ly) => (
              <li key={ly}>{ly}</li>
            ))}
          </ul>
          <p className="mt-1">
            Sao kê đã qua bước đối soát là số dùng để trả tiền. Đưa sao kê về trạng thái{' '}
            <em>pending</em> hoặc giữ kênh ở trạng thái lưu trữ.
          </p>
        </div>
      )}

      {xemTruoc.data && !xemTruoc.data.daLuuTru && (
        <p className="text-bad-chu">Phải lưu trữ kênh trước khi xoá vĩnh viễn.</p>
      )}

      {loai.length > 0 && (
        <div>
          <p className="font-semibold text-chu">Số dòng sẽ xoá</p>
          <ul className="mt-1 space-y-1">
            {loai.map(([ten, so]) => (
              <li key={ten} className="flex justify-between gap-4">
                <span>{ten}</span>
                <span className="so-lieu font-semibold text-chu">{so.toLocaleString('vi-VN')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-[10px] border border-vien bg-warn-nen p-3 text-warn-chu">
        <p className="font-semibold">Sao lưu trước khi xoá</p>
        <p className="mt-1">
          Chạy <code>npm run backup:db</code> để có bản sao cả database, và tải riêng dữ liệu kênh
          này về Excel:
        </p>
        <a
          href={duongDanXuatExcelKenh(channelId)}
          className="vung-bam mt-2 inline-flex items-center rounded-[10px] border border-vien bg-be-mat px-4 text-co-ban font-semibold text-chu hover:bg-neutral-nen"
        >
          Tải Excel dữ liệu kênh
        </a>
      </div>
    </HopXacNhan>
  );
}
